import React, { useEffect, useState } from 'react';
import { Icon, Header, Button, Table, Dropdown, Modal, useFrontier } from 'react-frontier2';
import { EventPaymentPlan, PaymentPlan, SetLoading } from '@arema/components/Classes';
import { addCommas, bindClick } from '@arema/components/Util';
import { useUser } from '../AdminHooks';
import API from '../API';
import UserAccess from '../UserAccess';
import UserLink from './UserLink';

export interface EventPaymentModalProps {
	open: boolean,
	onClose: ()=>void,
	title?: string,
	eventId: number,
}

var EventPaymentModal = (props: EventPaymentModalProps)=>{
	var { user, hasAccess } = useUser();
	var { Toast } = useFrontier();
	var [paymentPlans, setPaymentPlans] = useState<PaymentPlan[]>(null);
	var [eventPaymentPlans, setEventPaymentPlans] = useState<EventPaymentPlan[]>(null);
	var [sending, setSending] = useState<PaymentPlan>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [deletingPlan, setDeletingPlan] = useState<number>(null);
	var HAS_ACCESS = hasAccess(UserAccess.EVENTS.EDIT_OPTIONS_MSI);

	useEffect(()=>{
		if(!HAS_ACCESS) return;
		if(props.open && props.eventId){
			// setPaymentPlans(null);
			setEventPaymentPlans(null);
			setLoadError(null);
			loadData().then(res=>{
				if(res.error) return setLoadError(res.message);
			}).catch((err)=>{
				setLoadError('Hubo un error cargando la informacion (LCL-EVPPLM-1)');
			})
		}
	}, [props.open, props.eventId]);

	var loadData = async ()=>{
		if(!paymentPlans){
			var res = await API.getPaymentPlans();
			if(res.error) return res;
			setPaymentPlans(res.data);
		}
		var res_event = await API.getEventPaymentPlans(props.eventId);
		if(res_event.error) return res;
		setEventPaymentPlans(res_event.data);
		return res_event;
	}

	var onOpenConfirmDelete = (plan_id: number)=>{
		return ()=>{
			setDeletingPlan(plan_id);
		}
	}

	var deletePaymentPlan = (setLoading: SetLoading)=>{
		if(!deletingPlan) return;
		setLoading(true);
		API.deleteEventPaymentPlan(props.eventId, deletingPlan).then((res)=>{
			if(res.error) return Toast.error(res.message);
			setEventPaymentPlans(eventPaymentPlans.filter(e=>e.plan_id!==deletingPlan));
			setDeletingPlan(null);
			Toast.success("Se elimino el plan de pago del evento");
		}).catch(()=>{
			Toast.error('Hubo un error eliminando el plan de pago. (LCL-EVPPLM-2)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var addPaymentPlan = (plan_id: number)=>{
		return ()=>{
			if(eventPaymentPlans.findIndex(a=>a.plan_id===plan_id)!=-1){
				return Toast.error('Este plan de pago ya esta seleccionado');
			}
			var plan = paymentPlans.find(a=>a.plan_id===plan_id);
			if(!plan) return;
			setSending(plan);
			API.saveEventPaymentPlan(props.eventId, plan.plan_id).then(res=>{
				if(res.error) return Toast.error(res.message);
				var epp = [...eventPaymentPlans];
				epp.push({
					event_id: props.eventId,
					...plan,
					user_added: user.user_id,
					user_added_name: `${user.first_name} ${user.last_name}`,
				});
				setEventPaymentPlans(epp);
			}).catch(err=>{
				Toast.error('Hubo un error guardando el plan de pago. (LCL-EVPPLM-3)');
			}).finally(()=>{
				setSending(null);
			})
		}
	}

	return <>
		<Modal open={props.open} onClose={props.onClose} size='small'>
			<Modal.Header>{props.title}</Modal.Header>
			<Modal.Content>
				{!HAS_ACCESS ? (
					<Header text={'Sin acceso'} subtext={'No tienes acceso a esta acción'} />
				) : loadError ? (
					<Header text={'Error'} subtext={loadError} />
				) : ((!paymentPlans || !eventPaymentPlans)) ? (
					<Header loading text='Cargando planes de pago' />
				) : <>
					<Dropdown
						value={null}
						placeholder="Planes de Pago"
						items={paymentPlans.map(a=>({
							key: a.plan_id,
							text: `${a.months} ${a.interest ? 'MCI' : 'MSI'}`,
							meta: `Mínimo: $${addCommas(a.minimum_payment, true, true)}`,
							disabled: eventPaymentPlans.findIndex(b=>b.plan_id===a.plan_id)!=-1,
							onClick: eventPaymentPlans.findIndex(b=>b.plan_id===a.plan_id)==-1 ? addPaymentPlan(a.plan_id) : null,
						}))}
					/>
					<Table fitted style={{ marginTop: 15 }} className='top'>
						<Table.Head
							title='Planes de pago del evento'
							titleSize='small'
							data={[['Meses', 'Intereses', 'Descripcion', 'Usuario', '']]}
							collapsingIndexes={[0, 1, 3, 4]}
						/>
						<Table.Body>
							{!!sending && (
								<Table.Row fitted 
									collapsingIndexes={[0, 1, 3, 4]}
									centeredIndexes={[0, 1, 3, 4]}
									data={[
										sending.months,
										<Icon name={sending.interest ? 'check' : 'remove'} />,
										(sending.description || `${sending.months} M${sending.interest ? 'C' : 'S'}I`),
										`${user.first_name} ${user.last_name}`,
										<div className="fr loading inline"></div>
									]}
								/>
							)}
							{eventPaymentPlans.map(plan=>(
								<Table.Row
									fitted
									key={`EVPPL-${plan.plan_id}`}
									collapsingIndexes={[0, 1, 3, 4]}
									centeredIndexes={[0, 1, 3, 4]}
									data={[
										plan.months,
										<Icon name={plan.interest ? 'check' : 'remove'} />,
										(plan.description || `${plan.months} M${plan.interest ? 'C' : 'S'}I`),
										<UserLink compact blank user={{
											user_id: plan.plan_id,
											fist_name: plan.user_added_name
										}} />,
										<Button iconName="pen" icon size='small' menu={[{
											text: 'Eliminar',
											onClick: onOpenConfirmDelete(plan.plan_id)
										}]} />
									]}
								/>
							))}
						</Table.Body>
					</Table>
				</>}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' onClick={props.onClose} />
			</Modal.Actions>
		</Modal>

		<Modal open={!!deletingPlan} onClose={bindClick(setDeletingPlan, null)} size="mini">
			<Modal.Header>Eliminar plan de pago</Modal.Header>
			<Modal.Content>
				<Header size='small' text='¿Eliminar el plan de pago de este evento?' subtext={'Se eliminara el plan de pago para este evento'} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setDeletingPlan, null)} />
				<Button text='Eliminar' color={'red'} onClick={deletePaymentPlan} />
			</Modal.Actions>
		</Modal>
	</>
}

export default EventPaymentModal;